import React, { useEffect, useState, useRef } from 'react';
import { fetchApi } from '../services/api';
import { Plus, Link as LinkIcon, Trash2, FolderOpen } from 'lucide-react';

export default function EventGroupList({ channelId, selectedGroupId, onSelectGroup, onGroupRefresh }) {
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const addingRef = useRef(false);

  useEffect(() => {
    if (!channelId) return;

    const fetchGroups = async () => {
      setLoading(true);
      try {
        const data = await fetchApi(`/groups/${channelId}`);
        setGroups(data);

        // Restore saved group selection or select first
        const savedGroup = localStorage.getItem(`pupl_selectedGroup_${channelId}`);
        if (savedGroup) {
          const match = data.find(g => String(g.id) === savedGroup);
          if (match) {
            onSelectGroup(match.id);
            return;
          }
        }
        if (data.length > 0) {
          onSelectGroup(prev => prev ?? data[0].id);
        }
      } catch (err) {
        console.error('Gruplar alınamadı:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  const handleAddGroup = async () => {
    if (!newGroupName.trim() || addingRef.current) return;

    if (groups.some(g => g.name === newGroupName)) {
      alert("Bu isimde grup zaten var.");
      return;
    }

    addingRef.current = true;
    setAdding(true);
    try {
      const data = await fetchApi('/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newGroupName, channelId }),
      });

      setNewGroupName('');
      setGroups(prev => {
        if (prev.some(g => g.id === data.id)) return prev;
        return [data, ...prev];
      });
      onSelectGroup(data.id);
    } catch (err) {
      console.error('Grup ekleme hatası:', err);
      alert(err.message || 'Grup eklenirken hata oluştu.');
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    const confirmDelete = window.confirm(
      `Gruba kayıtlı tüm etkinlikler ve grup linki silinecektir.\n"${groupName}" grubunu silmek istediğinizden emin misiniz?`
    );
    if (!confirmDelete) return;

    try {
      await fetchApi(`/groups/${groupId}`, { method: 'DELETE' });
      setGroups(prev => prev.filter(g => g.id !== groupId));
      if (selectedGroupId === groupId) {
        onSelectGroup(null);
      }
    } catch (err) {
      console.error('Grup silme hatası:', err);
      alert(err.message || 'Grup silinirken hata oluştu.');
    }
  };

  const handleCopyLink = (groupId) => {
    const link = `${window.location.origin}/public/${channelId}/${groupId}`;
    navigator.clipboard.writeText(link);
    alert("Link kopyalandı!");
  };

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      {/* Add Group Row */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Yeni grup adı..."
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddGroup()}
          style={{
            flex: 1,
            padding: '0.6rem 0.85rem',
            borderRadius: 'var(--radius-sm)',
            border: '1.5px solid var(--border-color)',
            fontSize: '1rem',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            outline: 'none',
            transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--primary-400)';
            e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--border-color)';
            e.target.style.boxShadow = 'none';
          }}
        />
        <button
          onClick={handleAddGroup}
          disabled={adding}
          style={{
            padding: '0.6rem 1rem',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: adding ? 'var(--neutral-300)' : 'var(--accent-500)',
            color: '#fff',
            fontWeight: '600',
            fontSize: '0.875rem',
            cursor: adding ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            transition: 'background var(--transition-fast)',
          }}
          onMouseEnter={(e) => !adding && (e.currentTarget.style.background = 'var(--accent-600)')}
          onMouseLeave={(e) => { if (!adding) e.currentTarget.style.background = 'var(--accent-500)'; }}
        >
          <Plus size={16} />
          {adding ? '...' : 'Ekle'}
        </button>
      </div>

      {/* Groups */}
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '1rem',
          color: 'var(--text-muted)',
          fontSize: '1rem',
        }}>
          Yükleniyor...
        </div>
      ) : groups.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '1.5rem',
          color: 'var(--text-muted)',
          fontSize: '1rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <FolderOpen size={24} />
          Henüz grup yok.
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          padding: '4px 0',
          display: 'flex',
          gap: '0.5rem',
        }}>
          {groups.map((group) => {
            const isSelected = group.id === selectedGroupId;
            return (
              <div
                key={group.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: isSelected
                    ? 'linear-gradient(135deg, var(--primary-500), var(--primary-600))'
                    : 'var(--bg-elevated)',
                  color: isSelected ? '#fff' : 'var(--text-primary)',
                  border: `1.5px solid ${isSelected ? 'transparent' : 'var(--border-color)'}`,
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.5rem 0.85rem',
                  cursor: 'pointer',
                  minWidth: '160px',
                  maxWidth: '220px',
                  transition: 'all var(--transition-fast)',
                  boxShadow: isSelected ? '0 3px 12px rgba(99, 102, 241, 0.25)' : 'var(--shadow-sm)',
                }}
                onClick={() => onSelectGroup(group.id)}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.borderColor = 'var(--primary-300)';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
              >
                <div style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                }}>
                  {group.name}
                </div>

                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCopyLink(group.id); }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '3px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      color: isSelected ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)',
                      transition: 'color var(--transition-fast)',
                    }}
                    title="Link Kopyala"
                    onMouseEnter={(e) => e.currentTarget.style.color = isSelected ? '#fff' : 'var(--primary-500)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = isSelected ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)'}
                  >
                    <LinkIcon size={14} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id, group.name); }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '3px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      color: isSelected ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)',
                      transition: 'color var(--transition-fast)',
                    }}
                    title="Sil"
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--danger-500)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = isSelected ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)'}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
